import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, signal } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import * as echarts from 'echarts';
import html2pdf from 'html2pdf.js';
import { Subscription, forkJoin, combineLatest } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PageLoaderService } from '../../../core/service/page-loader.service';
import { SharedStateService } from '../../../shared/services/shared-state.service';
import { BackendModelItem, BlowerTableRow, TransposedRow } from '../../models/blower-model.model';
import { TransformData } from '../../models/transform-form-data';
import { ChartDataService } from '../../services/chart-data.service';
import { ProductsService } from '../../services/products.service';
import { UnitConversionService } from '../../services/unit-conversion.service';
import { getRuntechTemplateHtml } from './runtech-template.const';

@Component({
  selector: 'app-blower-filter-table',
  templateUrl: './blower-filter-table.component.html',
  styleUrls: ['./blower-filter-table.component.scss'],
  standalone: false,
})
export class BlowerFilterTableComponent implements OnInit, OnDestroy, OnChanges {
  showAdiabaticEfficiency = true;
  private readonly DEFAULT_VALUE = '-';
  private readonly TAB_CONDITION = 'condition';
  private readonly TAB_MODEL = 'model';
  private subscriptions: Subscription[] = [];
  selectedRows: BlowerTableRow[] = [];
  maxflow: number;
  allDataItems: BlowerTableRow[] = [];
  public transposedData = signal<TransposedRow[]>([]);
  public transposedColumns: string[] = ['header-label', 'header-unit', 'header-value'];
  private readonly baseColumns: string[] = [
    'model',
    'rotationSpeed',
    'activeGridPower',
    'shaftTorque',
    'dischargeTemp',
    'massFlow',
    'moisture',
    'moistureContent',
    'dischargeVolumetricFlow',
    'dischargeRelativeHumidity',
    'dischargeDewPoint',
    'totalEfficiency',
    'adiabaticEfficiency',
  ];

  private readonly heatRecoveryColumns: string[] = ['condensateMassFlow', 'airCoolingPower', 'latentPower', 'totalCoolingPower'];

  public displayColumns: string[] = [];
  heatRecoveryUsed: boolean = true;
  private formStateSub: Subscription;
  // Holds the most-recent unitType reported by the form but not yet applied to the table
  private pendingUnitType?: string;
  // The unitType currently applied to the table / PDF display
  private appliedUnitType?: string;
  showTable = signal(false);
  isCalculating = signal(false);
  private submitSub: Subscription;
  private sensorValuesSub: Subscription;
  adiabaticEfficiencyEnabled: boolean = true;
  totalEfficiencyEnabled: boolean = true;

  public columnLabels: { [key: string]: string } = {
    moisture: 'Moisture Content (=abs. moisture)',
    moistureContent: 'Moisture Content',
    dischargeVolumetricFlow: 'Discharge Volumetric Flow',
    dischargeRelativeHumidity: 'Discharge Relative Humidity',
    dischargeDewPoint: 'Dew Point',
    shaftTorque: 'Shaft Torque',
    dischargeTemp: 'Discharge Temperature',
    activeGridPower: 'Active Grid Power',
    rotationSpeed: 'Rotation Speed',
    totalEfficiency: 'Total Efficiency',
    adiabaticEfficiency: 'Adiabatic Efficiency',
    massFlow: 'Mass Flow',
    condensateMassFlow: 'Condensate Mass Flow',
    airCoolingPower: 'Air Cooling Power',
    latentPower: 'Latent Power',
    totalCoolingPower: 'Total Cooling Power',
  };

  public columnToUnitMap: { [key: string]: string } = {
    moisture: 'Moisture Content (=abs. moisture)',
    moistureContent: 'Moisture Content',
    dischargeVolumetricFlow: 'Discharge Volumetric Flow',
    dischargeRelativeHumidity: 'Discharge Relative Humidity',
    dischargeDewPoint: 'Dew Point',
    shaftTorque: 'Shaft Torque',
    dischargeTemp: 'Discharge Temperature',
    activeGridPower: 'Active Grid Power',
    rotationSpeed: 'Rotation Speed',
    totalEfficiency: 'Total Efficiency',
    adiabaticEfficiency: 'Adiabatic Efficiency',
    massFlow: 'Mass Flow',
    condensateMassFlow: 'Condensate Mass Flow',
    airCoolingPower: 'Air Cooling Power',
    latentPower: 'Latent Power',
    totalCoolingPower: 'Total Cooling Power',
  };

  public displayedColumns: string[] = ['select', ...this.displayColumns, 'download'];
  // Flag to indicate PDF download in progress
  public isPdfDownloading = signal(false);

  // Public API expected by older specs
  public setDisplayColumns(): void {
    if (this.heatRecoveryUsed) {
      this.displayColumns = [...this.baseColumns, ...this.heatRecoveryColumns];
    } else {
      this.displayColumns = [...this.baseColumns];
    }
    this.displayedColumns = ['select', ...this.displayColumns, 'download'];
  }

  public onSelectCheckbox(checked: boolean, row: BlowerTableRow): void {
    if (checked) {
      this.pushRowWithoutModelString(row);
    } else {
      const id = this.getUniqueIdentifier(row);
      this.filteredSelectedRows = this.filteredSelectedRows.filter((r) => this.getUniqueIdentifier(r) !== id);
      this.productservice.setFilteredSelectedRows(this.filteredSelectedRows);
    }
  }

  @Input() tab: string = '';
  dataSource = new MatTableDataSource<BlowerTableRow>([]);
  filteredSelectedRows: BlowerTableRow[] = [];
  modelAvailable: boolean = false;
  modelName: string = '';
  @Input() sensorValues: TransformData;
  @Input() activeTab: string = '';
  @Output() modelEmitter = new EventEmitter<string>();
  public loadingContent = false;
  fetchSensorValues: any;
  powerSupply!: string;
  showNoModelsMessage: boolean = false;
  objectKeys = Object.keys;
  blowerTypeLabel: string = '';
  selectedUnit: string = 'm³/s';
  private backendResponse: any;

  // Centralized unit mappings for table display (used by updateUnitsForUnitType)
  private readonly unitsTableSI = {
    'Moisture Content (=abs. moisture)': 'gH20/kgda air',
    'Moisture Content': 'gH20/kg air',
    'Discharge Volumetric Flow': 'm³/s',
    'Discharge Relative Humidity': '%',
    'Dew Point': '°C',
    'Shaft Torque': 'Nm',
    'Discharge Temperature': '°C',
    'Active Grid Power': 'kW',
    'Rotation Speed': 'rpm',
    'Total Efficiency': '',
    'Adiabatic Efficiency': '',
    'Mass Flow': 'kg/s',
    'Condensate Mass Flow': 'kg/s',
    'Air Cooling Power': 'kW',
    'Latent Power': 'kW',
    'Total Cooling Power': 'kW',
  };

  private readonly unitsTableUS = {
    'Moisture Content (=abs. moisture)': 'lb/lb_dry_air',
    'Moisture Content': 'lb/lb_dry_air',
    'Discharge Volumetric Flow': 'CFM',
    'Discharge Relative Humidity': '%',
    'Dew Point': '°F',
    'Shaft Torque': 'Nm',
    'Discharge Temperature': '°F',
    'Active Grid Power': 'kW',
    'Rotation Speed': 'Hz',
    'Total Efficiency': '',
    'Adiabatic Efficiency': '',
    'Mass Flow': 'lb/s',
    'Condensate Mass Flow': 'lb/s',
    'Air Cooling Power': 'BTU/h',
    'Latent Power': 'BTU/h',
    'Total Cooling Power': 'BTU/h',
  };

  // Centralized unit mappings for PDF template (used by downloadPdf)
  private readonly unitMapSI = {
    'suction-air-temp': '°C',
    'suction-air-relative-humdity': '%',
    vacuum: 'kPa',
    'suction-vol-flow': 'm³/s',
    altitude: 'm',
    'dis-abs-pressure': 'Pa',
    'atm-pressure': 'Pa',
    'rot-speed': 'rpm',
    'active-grid-power': 'kW',
    'shaft-torque': 'Nm',
    'discharge-temp': '°C',
    'mass-flow': 'kg/s',
    'discharge-vol-flow': 'm³/s',
    'discharge-rh': '%',
    'discharge-dew-point': '°C',
    'moisture-content-abs': 'gH20/kgda air',
    'moisture-content': 'gH20/kg air',
    'abs-pre-after-recovery': 'Pa',
    'temp-after-recovery': '°C',
    'rh-after-recovery': '%',
    'condensate-massflow': 'kg/s',
    'air-cooling-power': 'kW',
    'latent-power': 'kW',
    'total-cooling-power': 'kW',
    'max-speed': 'rpm',
    'static-load': 'kN',
    'max-dynamic-load': 'kN',
    'nominal-cooling-air-flow': 'm³/s',
    'cooling-air-temp': '°C',
    'cooling-air-pressure-drop': 'Pa',
    'inlet-flange': '',
    'outlet-flange': '',
  };

  private readonly unitMapUS = {
    'suction-air-temp': '°F',
    'suction-air-relative-humdity': '%',
    vacuum: 'inHg',
    'suction-vol-flow': 'CFM',
    altitude: 'ft',
    'dis-abs-pressure': 'inHg',
    'atm-pressure': 'inHg',
    'rot-speed': 'Hz',
    'active-grid-power': 'kW',
    'shaft-torque': 'Nm',
    'discharge-temp': '°F',
    'mass-flow': 'lb/s',
    'discharge-vol-flow': 'CFM',
    'discharge-rh': '%',
    'discharge-dew-point': '°F',
    'moisture-content-abs': 'lb/lb_dry_air',
    'moisture-content': 'lb/lb_dry_air',
    'abs-pre-after-recovery': 'inHg',
    'temp-after-recovery': '°F',
    'rh-after-recovery': '%',
    'condensate-massflow': 'lb/s',
    'air-cooling-power': 'BTU/h',
    'latent-power': 'BTU/h',
    'total-cooling-power': 'BTU/h',
    'max-speed': 'rpm',
    'static-load': 'kN',
    'max-dynamic-load': 'kN',
    'nominal-cooling-air-flow': 'CFM',
    'cooling-air-temp': '°F',
    'cooling-air-pressure-drop': 'inHg',
    'inlet-flange': '',
    'outlet-flange': '',
  };

  units = { ...this.unitsTableSI };

  /**
   * Update the unit display map when a top-level unitType is provided (e.g. 'US').
   * This only updates the displayed unit strings — numeric conversions are handled elsewhere.
   */
  private updateUnitsForUnitType(unitType?: string): void {
    if (!unitType) {
      return;
    }

    const chosenUnitMap = unitType.toUpperCase() === 'US' ? this.unitsTableUS : this.unitsTableSI;
    this.units = { ...chosenUnitMap };

    // If we already have transposed data, refresh the view
    if (this.transposedData().length > 0) {
      this.transposeData();
    }
    // Keep a user-friendly selectedUnit value in sync for tests/UI
    this.selectedUnit = unitType.toUpperCase() === 'US' ? 'CFM' : 'm³/s';
  }
  private readonly MAX_SELECTED_ROWS = 4;

  private motorStates = {
    motor400kW: false,
    motor500kW: false,
    motor580kW: false,
    motor650kW: true,
  };

  private motorSubscriptions: Subscription[] = [];

  private readonly conversionRules: Array<{ match: (si: string, us: string) => boolean; convert: (v: number) => number }> = [
    { match: (si, us) => si.includes('°C') && us.includes('°F'), convert: (v) => (v * 9) / 5 + 32 },
    { match: (si, us) => (si === 'm' || si.includes('m')) && us === 'ft', convert: (v) => v * 3.28084 },
    { match: (si, us) => si === 'Pa' && us === 'inHg', convert: (v) => v / 3386.389 },
    { match: (si, us) => si === 'kPa' && us === 'inHg', convert: (v) => (v * 1000) / 3386.389 },
    { match: (si, us) => si === 'm³/s' && (us === 'CFM' || us === 'cfm'), convert: (v) => v * 2118.88 },
    { match: (si, us) => si === 'kg/s' && us === 'lb/s', convert: (v) => v * 2.2046226218 },
    { match: (si, us) => si === 'kW' && us.toLowerCase().includes('btu'), convert: (v) => v * 3412.142 },
    { match: (si, us) => si === 'Nm' && us.toLowerCase().includes('lbf'), convert: (v) => v * 0.737562149277 },
    { match: (si, us) => si === 'rpm' && us === 'Hz', convert: (v) => v / 60 },
    { match: (si, us) => si === '%' && us === '%', convert: (v) => v },
    { match: (si, us) => si.includes('kg/kg') && us.toLowerCase().includes('lb/lb'), convert: (v) => v },
  ];

  private readonly templateRegexCache = new Map<string, RegExp>();

  constructor(
    private productservice: ProductsService,
    private unitConversionService: UnitConversionService,
    private pageLoaderService: PageLoaderService,
    private sharedStateService: SharedStateService,
    private chartDataService: ChartDataService,
    private http: HttpClient,
  ) {
    this.pageLoaderService.onInit();
    this.pageLoaderService.onPageEvent((loading) => {
      this.loadingContent = loading;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sensorValues'] && changes['sensorValues'].currentValue) {
      const sv: TransformData = changes['sensorValues'].currentValue;
      // Do not apply immediately — store as pending and apply on submit
      const unitType = (sv as any).unitType;
      if (unitType) {
        this.pendingUnitType = unitType;
      }
    }
  }

  ngOnInit(): void {
    // Initialize displayColumns with all columns
    this.displayColumns = [...this.baseColumns];

    const maxFlowUnitSub = this.unitConversionService?.selectedMaxFlowUnit$?.subscribe((unit) => {
      this.selectedUnit = unit;
    });
    if (maxFlowUnitSub) {
      this.subscriptions.push(maxFlowUnitSub);
    }

    // Listen for form state updates (unitType / selectedUnits) so the table units
    // and PDF template can be updated when the user switches between SI/US.
    this.formStateSub = this.productservice.formState$Model.subscribe((state) => {
      try {
        const unitType = state?.formValues?.unitType;
        if (unitType) {
          this.pendingUnitType = unitType;
        }
      } catch (e) {
        // ignore
      }
    });

    // Combine all display-flag streams into one subscription — all three are BehaviorSubjects
    // so combineLatest emits immediately on subscribe, then once per individual change.
    this.subscriptions.push(
      combineLatest([
        this.sharedStateService.heatRecoveryUsed$,
        this.sharedStateService.adiabaticEfficiency$,
        this.sharedStateService.totalEfficiency$,
      ]).subscribe(([heatRecovery, adiabaticEfficiency, totalEfficiency]) => {
        this.heatRecoveryUsed = heatRecovery;
        this.displayColumns = heatRecovery ? [...this.baseColumns, ...this.heatRecoveryColumns] : [...this.baseColumns];
        this.adiabaticEfficiencyEnabled = adiabaticEfficiency;
        this.totalEfficiencyEnabled = totalEfficiency;
        if (this.transposedData().length > 0) {
          this.transposeData();
        }
      }),
    );

    this.submitSub = this.sharedStateService.submitClicked$.subscribe(() => {
      if (this.pendingUnitType) {
        this.updateUnitsForUnitType(this.pendingUnitType);
        this.appliedUnitType = this.pendingUnitType;
      }
      this.showTable.set(false);
      this.isCalculating.set(true);
    });

    // Subscribe to motor state changes
    this.motorSubscriptions.push(
      this.sharedStateService.motor400kW$.subscribe((val) => {
        this.motorStates.motor400kW = val;
      }),
      this.sharedStateService.motor500kW$.subscribe((val) => {
        this.motorStates.motor500kW = val;
      }),
      this.sharedStateService.motor580kW$.subscribe((val) => {
        this.motorStates.motor580kW = val;
      }),
      this.sharedStateService.motor650kW$.subscribe((val) => {
        this.motorStates.motor650kW = val;
      }),
    );

    if (this.tab === this.TAB_MODEL) {
      this.setupModelTab();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.submitSub?.unsubscribe();
    this.motorSubscriptions.forEach((sub) => sub.unsubscribe());
    this.formStateSub?.unsubscribe();
    this.sensorValuesSub?.unsubscribe();
  }
  public isMotorSpeedTooHigh(row: TransposedRow): boolean {
    const model = String(row['model'] ?? '');
    const rotationSpeed = parseFloat(String(row.col0));

    if (!rotationSpeed) {
      return false;
    }

    if (model.startsWith('EP315-D1') || model === 'EP315-S') {
      return rotationSpeed > 10200;
    } else if (model === 'EP1000-H1 (Open)') {
      return rotationSpeed > 11500;
    } else {
      return rotationSpeed > 9600;
    }
  }

  public isShaftTorqueHigh(row: TransposedRow): boolean {
    const model = String(row['model'] ?? '');
    const shaftTorque = parseFloat(String(row.col0));

    if (!shaftTorque) {
      return false;
    }

    // Special model cases first - these checks apply regardless of motor selection
    if (['EP315-D1', 'EP315-D1+1', 'EP315-S'].includes(model)) {
      return shaftTorque > 298;
    }
    if (model === 'EP1000-H1 (Open)') {
      return shaftTorque > 830;
    }

    // Models that should show warning when shaft torque > 582, regardless of motor selection
    const models582Limit = ['EP500-D1', 'EP600-T1', 'E6500-H1', 'EP600-S1', 'EP600-S2', 'EP600-HF1', 'EP600-HF2'];
    if (models582Limit.includes(model)) {
      return shaftTorque > 582;
    }

    // Model EP400-D2 has its own limit regardless of motor selection
    if (model === 'EP400-D2') {
      return shaftTorque > 490;
    }

    // Motor-based checks for all other models
    if (this.motorStates.motor400kW) {
      return shaftTorque > 398;
    }
    if (this.motorStates.motor500kW) {
      return shaftTorque > 490;
    }
    if (this.motorStates.motor580kW) {
      return shaftTorque > 582;
    }
    if (this.motorStates.motor650kW) {
      return shaftTorque > 599;
    }

    return false;
  }

  /**
   * Return a user-friendly Bleed Air string for the template.
   * Keeps presentation logic out of the HTML template to reduce complexity.
   */
  public getBleedAirText(row: BlowerTableRow): string {
    if (!row) {
      return 'Cannot Calculate';
    }
    const val = row.bleedAir;
    if (val === 'Cannot Calculate' || val == null || val === '') {
      return 'Cannot Calculate';
    }
    return `${val} m³/s`;
  }

  transposeData() {
    if (this.backendResponse?.exactModel?.model || this.selectedRows?.[0]?.model) {
      this.modelName = this.backendResponse?.exactModel?.model ?? this.selectedRows[0]?.model ?? this.DEFAULT_VALUE;
    }

    // Filter columns based on flags
    const columns = this.displayColumns.filter((col) => {
      // Only filter by efficiency flags, heat recovery is handled by displayColumns
      if (col === 'adiabaticEfficiency') {
        return this.adiabaticEfficiencyEnabled;
      }
      if (col === 'totalEfficiency') {
        return this.totalEfficiencyEnabled;
      }
      return true;
    });

    const rows = this.dataSource.data;

    // Build transposed data for all columns (we later filter out 'model' entries);
    // the actual transposed dataset used for display is built below as
    // `transposedWithoutModel` so we don't keep an extra unused local variable.

    // Build transposed column keys
    this.transposedColumns = ['label', ...rows.map((_, i) => 'col' + i)];

    // Filter out any existing MODEL rows from the columns before creating transposed data
    const filteredColumns = columns.filter((col) => col !== 'model');

    // Build transposed data with filtered columns
    const transposedWithoutModel = filteredColumns.map((column) => {
      const row: TransposedRow = {
        label: this.columnLabels[column] || column,
        unit: this.columnToUnitMap[column] ? this.units[this.columnToUnitMap[column]] : '',
      };
      rows.forEach((data, index) => {
        row[`col${index}`] = this.getColumnValue(data, column);
      });
      return row;
    });

    // Inject backend values for cooling powers & condensate mass flow
    if (this.backendResponse) {
      const injectMap = {
        totalCoolingPower: this.backendResponse.totalCoolingPower,
        latentPower: this.backendResponse.latentPower,
        airCoolingPower: this.backendResponse.airCoolingPower,
        condensateMassFlow: this.backendResponse.condensateMassFlow,
      };

      Object.entries(injectMap).forEach(([key, value]) => {
        const idx = filteredColumns.findIndex((col) => col === key);
        if (idx !== -1 && value !== undefined) {
          // Inject the value into the first available column (col0)
          transposedWithoutModel[idx].col0 = value;
        }
      });
    }

    this.transposedData.set(transposedWithoutModel);
    this.isCalculating.set(false);
    if (transposedWithoutModel.length > 0) {
      this.showTable.set(true);
    }
  }

  private setupModelTab(): void {
    this.subscriptions.push(
      this.productservice.modelData$.subscribe((modelData) => {
        this.backendResponse = modelData?.[0];
        this.selectedRows = [];
        this.filteredSelectedRows = [];
        this.productservice.clearSelectedCheckboxes();
        this.productservice.setFilteredSelectedRows([]);
        this.dataSource.data = modelData ? this.processData(structuredClone(modelData) as BackendModelItem[]) : [];
        this.sensorValuesSub?.unsubscribe();
        this.sensorValuesSub = this.productservice.sensorValues$.subscribe((data) => {
          this.fetchSensorValues = structuredClone(data);
        });
        if (this.pendingUnitType) {
          this.updateUnitsForUnitType(this.pendingUnitType);
          this.appliedUnitType = this.pendingUnitType;
        }
        this.transposeData();
        this.processModelData();
      }),
    );
  }

  private processModelData(): void {
    this.dataSource?.data.forEach((item: BlowerTableRow) => {
      item.isHeaterRequired = false;
      if (item?.exactModel) {
        this.modelName = item?.exactModel?.model;
        this.modelEmitter.emit(this.modelName);
      }
    });
  }

  pushRowWithoutModelString(row: BlowerTableRow): void {
    // Create a deep clone of the row to avoid modifying the original display data
    const clonedRow = structuredClone(row);

    // Store the original model name with heater information for UI display
    const originalModelName = clonedRow.exactModel?.model || clonedRow.model || '';

    // Keep track of whether a heater is required
    const hasHeater = clonedRow.isHeaterRequired || (originalModelName && originalModelName.includes('Low inlet temp heater'));

    // Preserve the heater requirement flag explicitly for the UI
    clonedRow.isHeaterRequired = hasHeater;

    // Clean the model names for backend processing
    this.cleanModelNames(clonedRow);

    const rowIdentifier = this.getUniqueIdentifier(row);
    const index = this.filteredSelectedRows.findIndex((r) => this.getUniqueIdentifier(r) === rowIdentifier);

    if (index === -1) {
      this.filteredSelectedRows.push(clonedRow);
    } else {
      this.filteredSelectedRows[index] = clonedRow;
    }

    // Update the service with the complete list
    this.productservice.setFilteredSelectedRows(this.filteredSelectedRows);
  }

  private cleanModelNames(row: BlowerTableRow): void {
    // Clean the model name for backend processing
    if (row.exactModel?.model && row.exactModel.model.includes('+')) {
      row.exactModel.model = row.exactModel.model.split('+')[0].trim();
    }

    if (row.model && row.model.includes('+')) {
      row.model = row.model.split('+')[0].trim();
    }
  }

  private getUniqueIdentifier(item: BlowerTableRow): string {
    if (item.exactModel?.['id']) {
      return `exact-${item.exactModel?.['id']}`;
    }
    if (item?.['id']) {
      return `row-${item?.['id']}`;
    }
    const modelName = item.exactModel?.model || item.model || '';
    const maxFlow = item.exactModel?.maxFlow || item.maxFlow || '';
    const blowerType = item.exactModel?.blowerType || item?.['blowerType'] || '';

    return `${modelName}-${maxFlow}-${blowerType}`;
  }

  getColumnValue(element: BlowerTableRow, column: string): number | string {
    const el = element as Record<string, string | number | boolean | null | undefined>;
    if (el[column] !== undefined) {
      return el[column] as number | string;
    }
    if (element.exactModel && typeof element.exactModel === 'object') {
      const exactModelRecord = element.exactModel as Record<string, string | number | boolean | null | undefined>;
      if (exactModelRecord[column] !== undefined) {
        return (exactModelRecord[column] as number | string) || this.DEFAULT_VALUE;
      }
    }
    return this.DEFAULT_VALUE;
  }

  private processData(data: BackendModelItem[]): BlowerTableRow[] {
    const processedData: BlowerTableRow[] = [];

    // Clear allDataItems before processing new data
    this.allDataItems = [];

    if (data.length === 0) {
      // Reset showNoModelsMessage when there's no data
      this.showNoModelsMessage = false;
      return processedData;
    }

    if (this.tab === this.TAB_MODEL) {
      this.processModelTabData(data, processedData);
    }

    processedData.sort((a, b) => (a.percentage || 0) - (b.percentage || 0));

    // Show message only if there was actual data sent but no valid models found
    this.showNoModelsMessage = data.length > 0 && processedData.length === 0;

    return processedData;
  }

  private processModelTabData(data: BackendModelItem[], processedData: BlowerTableRow[]): void {
    // Small accessor map to avoid a long if/else chain. Declared once to
    // reduce nesting and cognitive complexity. Accessors receive the model
    // item and the interpolation object (when relevant).
    const accessors: { [key: string]: (it: any, interp: any) => any } = {
      totalCoolingPower: (it) => it.totalCoolingPower,
      latentPower: (it) => it.latentPower,
      airCoolingPower: (it) => it.airCoolingPower,
      condensateMassFlow: (it) => it.condensateMassFlow,
      moisture: (it) => it.state1MoistureContentAbs,
      moistureContent: (it) => it.state1MoistureContent,
      dischargeRelativeHumidity: (it) => it.state2RelativeHumidity,
      dischargeDewPoint: (it) => it.dischargeDewPoint,
      rotationSpeed: (_it, i) => i.speedInterp?.[0],
      activeGridPower: (_it, i) => i.activeGridPowerInterp?.[0],
      shaftTorque: (_it, i) => i.shaftTorqueInterp?.[0],
      dischargeTemp: (_it, i) => i.newOutputTempInterp?.[0],
      massFlow: (_it, i) => i.newMassFlowInterp?.[0],
      dischargeVolumetricFlow: (_it, i) => i.outletVolumetricFlowInterp?.[0],
      totalEfficiency: (_it, i) => i.totalEfficiencyInterp?.[0],
      adiabaticEfficiency: (_it, i) => i.adiabaticEfficiencyInterp?.[0],
    };

    for (const item of data) {
      const interp = item.interpolationArrays?.interpolations || {};
      const modelDetails: BlowerTableRow = {};

      // Use simple iteration and optional chaining so there is no nested
      // conditional logic per column.
      for (const col of this.displayColumns) {
        modelDetails[col] = accessors[col]?.(item, interp) ?? this.DEFAULT_VALUE;
      }

      processedData.push(modelDetails);
      // mark at least one model as available when data exists
      this.modelAvailable = true;
    }
  }

  renderChartsForPdf(container: HTMLDivElement): void {
    const chartIds = ['chart1', 'chart2', 'chart3', 'chart4', 'chart5', 'chart6'];

    chartIds.forEach((id) => {
      const dom = container.querySelector(`#${id}`);
      if (dom instanceof HTMLElement) {
        let chart = echarts.getInstanceByDom(dom);
        if (!chart) {
          chart = echarts.init(dom);
        } else {
          chart.clear(); // Clear previous chart
        }

        const options = this.chartDataService.getChartOptions(id);
        chart.setOption(options);

        chart.resize();
      }
    });
  }

  // Protected wrapper to allow spying in tests
  protected getEchartsInstance(dom: HTMLElement) {
    return echarts.getInstanceByDom(dom);
  }

  // Convert chart canvases to base64 images and replace chart divs with img tags
  private convertChartsToImages(container: HTMLDivElement): void {
    const chartIds = ['chart1', 'chart2', 'chart3', 'chart4', 'chart5', 'chart6'];
    let successCount = 0;

    chartIds.forEach((id) => {
      try {
        // Search for chart element within the container
        const dom = container.querySelector(`#${id}`);
        if (!(dom instanceof HTMLElement)) {
          console.warn(`Chart container not found for ${id}`);
          return;
        }

        const chart = this.getEchartsInstance(dom);
        if (!chart) {
          console.warn(`Chart instance not found for ${id}`);
          return;
        }

        // Get the data URL from the chart
        const dataUrl = chart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        });

        if (!dataUrl) {
          console.warn(`Failed to get data URL for chart ${id}`);
          return;
        }

        // Create an img element to replace only the chart canvas div (not the whole card)
        const img = document.createElement('img');
        img.src = dataUrl;
        img.style.width = '100%';
        img.style.height = '380px';
        img.style.display = 'block';
        img.style.objectFit = 'contain';
        img.style.marginTop = '8px';
        img.style.marginLeft = '0';
        img.alt = `Chart ${id}`;

        // Replace only the chart canvas div inside the card, preserving the
        // chart-title sibling element and the card wrapper itself.
        if (dom.parentNode) {
          dom.parentNode.replaceChild(img, dom);
        }
        successCount++;
      } catch (err) {
        console.warn(`Failed to convert chart ${id} to image:`, err);
      }
    });
  }

  // Helper: build replaced HTML from template and data
  private buildReplacedHtmlFromTemplate(html: string, d: any, formattedDate: string): string {
    const replacements = this.getReplacementMap(d, formattedDate);

    let result = html;
    Object.entries(replacements).forEach(([key, val]) => {
      try {
        result = result.replace(this.getTemplateRegex(key), String(val));
      } catch (e) {
        // Ignore individual replacement errors and continue
      }
    });
    return result;
  }

  private getTemplateRegex(key: string): RegExp {
    let re = this.templateRegexCache.get(key);
    if (!re) {
      re = new RegExp(key, 'g');
      this.templateRegexCache.set(key, re);
    }
    return re;
  }

  // Build the replacement map in a dedicated helper to keep the caller small and
  // reduce cognitive complexity. Behaviour is preserved exactly.
  private getReplacementMap(d: any, formattedDate: string): { [k: string]: any } {
    const DEFAULT: any = this.DEFAULT_VALUE;
    const interp = d.interpolationArrays?.interpolations ?? {};

    // Delegate to small helpers to keep this method simple and reduce
    // cognitive complexity while preserving behaviour.
    return { ...this.buildTopLevelReplacement(d, formattedDate, DEFAULT), ...this.buildInterpReplacement(interp, DEFAULT) };
  }

  // Small helper that builds the top-level replacement entries.
  private buildTopLevelReplacement(d: any, formattedDate: string, DEFAULT: any): { [k: string]: any } {
    // Use a mapping table to keep logic flat and small to reduce cognitive
    // complexity while preserving the exact lookup semantics.
    const mappings: Array<[string, string]> = [
      ['p_model', 'exactModel.model'],
      ['p_suction_air_temp', 'suction_air_temperature'],
      ['p_suction_air_rh', 'suction_air_relative_humidity'],
      ['p_suction_air_relative_humidity', 'suction_air_relative_humidity'],
      ['p_vacuum', 'vacuum'],
      ['p_suction_vol_flow', 'suction_volumetric_flow'],
      ['p_altitude', 'altitude_from_sea_level'],
      ['p_dis_abs_pressure', 'dischargeAbsPressure'],
      ['p_atm_pressure', 'atmosphericPressure'],
      ['p_discharge_rh', 'state2RelativeHumidity'],
      ['p_discharge_dew_point', 'dischargeDewPoint'],
      ['p_moisture_content_abs', 'state1MoistureContentAbs'],
      ['p_moisture_content', 'state1MoistureContent'],
      ['p_abs_pre_after_recovery', 'absolute_pressure_after_recovery'],
      ['p_temp_after_recovery', 'temperature_after_recovery'],
      ['p_rh_after_recovery', 'relative_humidity_after_recovery'],
      ['p_condensate_massflow', 'condensateMassFlow'],
      ['p_air_cooling_power', 'airCoolingPower'],
      ['p_latent_power', 'latentPower'],
      ['p_total_cooling_power', 'totalCoolingPower'],
      ['p_max_speed', 'exactModel.maxSpeed'],
      ['p_static_load', 'exactModel.staticLoad'],
      ['p_maximum_dynamic_load', 'exactModel.maximumDynamicLoad'],
      ['p_nominal_cooling_air_flow', 'exactModel.nominalCoolingAirFlow'],
      ['p_cooling_air_temperature', 'exactModel.coolingAirTemperature'],
      ['p_cooling_air_pressure_drop', 'exactModel.coolingAirPressureDrop'],
      ['p_inlet_flange', 'exactModel.inletFlange'],
      ['p_outlet_flange', 'exactModel.outletFlange'],
    ];

    const result: { [k: string]: any } = {};
    for (const [key, path] of mappings) {
      // Use the existing helper to resolve deep paths and fall back to DEFAULT
      const val = this.getPathValue(d, path);
      result[key] = val !== undefined ? val : DEFAULT;
    }
    // p_download_date is set directly
    result['p_download_date'] = formattedDate;

    return result;
  }

  // Small helper that builds the interpolation replacement entries.
  private buildInterpReplacement(interp: any, DEFAULT: any): { [k: string]: any } {
    return {
      p_rot_speed: interp.speedInterp ?? DEFAULT,
      p_active_grid_power: interp.activeGridPowerInterp ?? DEFAULT,
      p_shaft_torque: interp.shaftTorqueInterp ?? DEFAULT,
      p_discharge_temp: interp.newOutputTempInterp ?? DEFAULT,
      p_mass_flow: interp.newMassFlowInterp ?? DEFAULT,
      p_discharge_vol_flow: interp.outletVolumetricFlowInterp ?? DEFAULT,
      p_total_eff: interp.totalEfficiencyInterp ?? DEFAULT,
      p_adiabatic_eff: interp.adiabaticEfficiencyInterp ?? DEFAULT,
    };
  }

  private createContainerWithHtml(replacedHtml: string): HTMLDivElement {
    const container = document.createElement('div');
    container.innerHTML = replacedHtml;
    return container;
  }

  private getAppliedUnitTypeOrSaved(): string {
    const savedState = this.productservice.getFormStateModel();
    const rawUnit = this.appliedUnitType ?? (savedState?.formValues as any)?.unitType;
    return rawUnit ? (rawUnit as string).toUpperCase() : 'SI';
  }

  private patchUnitSystemPlaceholder(container: HTMLDivElement, unitType: string): void {
    try {
      const unitLabel = unitType === 'US' ? 'US' : 'SI';
      container.innerHTML = container.innerHTML.replace(/p_unit_system/g, unitLabel);
    } catch (e) {
      console.warn('Failed to replace p_unit_system placeholder in template', e);
    }
  }

  // Simplified wrappers for deep path get/set used by conversions
  private getPathValue(obj: any, path: string) {
    return path.split('.').reduce((acc, p) => (acc && acc[p] !== undefined ? acc[p] : undefined), obj);
  }

  private setPathValue(obj: any, path: string, value: any) {
    const parts = path.split('.');
    const last = parts.pop();
    if (!last) {
      return;
    }
    let cur = obj;
    for (const p of parts) {
      if (!cur[p]) {
        cur[p] = {};
      }
      cur = cur[p];
    }
    cur[last] = value;
  }

  private localConvertNumber(value: number, siUnit: string, usUnit: string): number | null {
    // Null/NaN guard
    if (value == null || Number.isNaN(value)) {
      return null;
    }
    const round2 = (v: number) => parseFloat(Number(v).toFixed(2));

    // Use a small helper to locate a matching conversion; keeps this function simple
    const convertFn = this.findConversionRule(siUnit, usUnit);
    if (convertFn) {
      return round2(convertFn(value));
    }

    // Default: coerce to number and round
    return round2(Number(value));
  }

  // Helper: find a conversion function for the provided units, or null when none match
  private findConversionRule(siUnit: string, usUnit: string): ((v: number) => number) | null {
    const matched = this.conversionRules.find((r) => {
      try {
        return r.match(siUnit, usUnit);
      } catch (e) {
        return false;
      }
    });

    return matched ? matched.convert : null;
  }

  private applyLocalConversions(convMap: any[], targetData: any) {
    convMap.forEach((m) => {
      const val = this.getPathValue(targetData, m.path);
      if (val === undefined || val === null || val === '') {
        return;
      }
      const numeric = m.isArray && Array.isArray(val) ? val[0] : val;
      const numericVal = typeof numeric === 'number' ? numeric : Number.parseFloat(String(numeric));
      if (Number.isNaN(numericVal)) {
        return;
      }
      if (m.siUnit === m.usUnit) {
        if (m.isArray && Array.isArray(val)) {
          const newArr = [...val];
          newArr[0] = numericVal;
          this.setPathValue(targetData, m.path, newArr);
        } else {
          this.setPathValue(targetData, m.path, numericVal);
        }
        return;
      }
      const converted = this.localConvertNumber(numericVal, m.siUnit, m.usUnit);
      if (converted === null) {
        return;
      }
      if (m.isArray && Array.isArray(val)) {
        const newArr = [...val];
        newArr[0] = converted;
        this.setPathValue(targetData, m.path, newArr);
      } else {
        this.setPathValue(targetData, m.path, converted);
      }
    });
  }

  private patchUnitLabels(container: HTMLDivElement, chosenMap: { [k: string]: string }) {
    Object.keys(chosenMap).forEach((cls) => {
      const el = container.querySelector(`.${cls}`);
      if (el) {
        const unitSpan = el.querySelector('.unit');
        if (unitSpan) {
          unitSpan.textContent = chosenMap[cls];
        }
      }
    });
  }

  private proceedAfterConversions(container: HTMLDivElement) {
    try {
      if (!this.adiabaticEfficiencyEnabled) {
        this.removeRowsWithCellClass(container, '.adiabatic-eff');
      }

      if (!this.totalEfficiencyEnabled) {
        this.removeRowsWithCellClass(container, '.total-eff');
      }

      if (!this.heatRecoveryUsed) {
        this.removeHeatRecoverySection(container);
      }

      if (!this.adiabaticEfficiencyEnabled && !this.totalEfficiencyEnabled) {
        this.removeChart6IfNeeded(container);
      }
    } catch (e) {
      console.warn('Failed to remove efficiency rows from template', e);
    }

    this.finalizeAndGenerate(container);
  }

  private removeRowsWithCellClass(container: HTMLDivElement, cellSelector: string) {
    const rows = Array.from(container.querySelectorAll('tr'));
    rows.forEach((r) => {
      try {
        if (r.querySelector && r.querySelector(cellSelector)) {
          r.remove();
        }
      } catch (err) {
        // ignore errors for specific row removal
      }
    });
  }

  private removeHeatRecoverySection(container: HTMLDivElement) {
    const headers = Array.from(container.querySelectorAll('.section-header'));
    headers.forEach((h) => {
      try {
        if (h.textContent && h.textContent.trim().toLowerCase().includes('heat recovery potential')) {
          const next = h.nextElementSibling;
          const spacer = document.createElement('div');
          spacer.className = 'heat-recovery-spacer';
          spacer.setAttribute('style', 'min-height:210px; margin:32px 0;');
          h.parentNode?.insertBefore(spacer, h);
          if (next && next.classList.contains('parameters-container')) {
            next.remove();
          }
          h.remove();
        }
      } catch (innerErr) {
        // ignore errors for specific header removal
      }
    });
  }

  private removeChart6IfNeeded(container: HTMLDivElement) {
    try {
      const chart6 = container.querySelector('#chart6');
      if (chart6) {
        const el = chart6;
        const card = el.closest('.chart-card') || el.closest('.chart-card2');
        if (card && card.parentNode) {
          card.parentNode.removeChild(card);
        }
      }
    } catch (err) {
      console.warn('Failed to remove chart6 card from template', err);
    }
  }

  /**
   * Remove the Product Views section (heading and images) from the container if both images are missing
   */
  private removeProductViewsSectionIfNoImages(container: HTMLDivElement) {
    const sectionHeader = Array.from(container.querySelectorAll('.section-header')).find(
      (el) => el.textContent?.trim().toLowerCase() === 'product views',
    );
    if (sectionHeader) {
      const section = sectionHeader.closest('.product-images-section');
      if (section) {
        section.remove();
      }
    }
    const blowerSection = container.querySelector('.blower-specs-section');
    if (blowerSection) {
      const pageBreak = document.createElement('div');
      pageBreak.style.cssText = 'page-break-before: always; break-before: page; height: 0;';
      blowerSection.parentNode?.insertBefore(pageBreak, blowerSection);
    }
  }

  private finalizeAndGenerate(container: HTMLDivElement) {
    // Ensure container has content
    try {
      if (!container.innerHTML || container.innerHTML.trim() === '') {
        // nothing to render
        return;
      }
    } catch (e) {
      console.warn('Failed to ensure container HTML before render', e);
    }

    document.body.appendChild(container);
    this.renderChartsForPdf(container);

    // Defer PDF generation to allow charts to render and convert to images
    setTimeout(() => {
      this.convertChartsToImages(container);
      // Give additional time for img tags to be rendered before PDF generation
      setTimeout(() => {
        this.generatePdf(container);
      }, 500);
    }, 1000);
  }

  // Extracted helpers to reduce complexity in the main method
  private getPdfOptions() {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    const baseName = this.modelName || 'runtech_datasheet';
    return {
      margin: 0.5,
      filename: `${baseName}_${timestamp}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2, UseCORS: true, allowTaint: true, logging: false },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' as const },
    };
  }

  private removeContainerSafely(container: HTMLDivElement, warnMessage?: string) {
    try {
      if (container && container.parentNode === document.body) {
        document.body.removeChild(container);
      }
    } catch (e) {
      console.warn(warnMessage || 'Failed to remove container from DOM', e);
    }
  }

  private handleSaveResult(saveResult: any, container: HTMLDivElement) {
    if (saveResult && typeof saveResult.then === 'function') {
      saveResult
        .then(() => {
          this.removeContainerSafely(container, 'Failed to remove container from DOM after save');
          this.isPdfDownloading.set(false);
        })
        .catch((err: any) => {
          console.error('html2pdf save failed', err);
          this.removeContainerSafely(container, 'Failed to remove container from DOM (fallback)');
          this.isPdfDownloading.set(false);
        });
    } else {
      // Some html2pdf implementations are synchronous; provide a delayed fallback
      setTimeout(() => {
        this.removeContainerSafely(container, 'Failed to remove container from DOM (delayed fallback)');
        this.isPdfDownloading.set(false);
      }, 1500);
    }
  }

  private generatePdf(container: HTMLDivElement) {
    const options = this.getPdfOptions();
    try {
      const pdfFn = (window as any).html2pdf || html2pdf;
      const saveResult = pdfFn().set(options).from(container).save();
      this.handleSaveResult(saveResult, container);
    } catch (err) {
      console.error('Failed to generate PDF', err);
      this.removeContainerSafely(container, 'Failed to remove container from DOM after PDF generation error');
      this.isPdfDownloading.set(false);
    }
  }

  downloadPdf(): void {
    // Show the download toast and start template processing
    this.isPdfDownloading.set(true);
    this.chartDataService.setChartData(this.backendResponse);

    // Check if backend is sending base64 images directly
    const image1Base64 = this.backendResponse?.image1Base64;
    const image2Base64 = this.backendResponse?.image2Base64;

    if (image1Base64 && image2Base64) {
      // Use base64 images directly from backend response
      const img1DataUrl = this.formatBase64DataUrl(image1Base64);
      const img2DataUrl = this.formatBase64DataUrl(image2Base64);
      const htmlWithImages = getRuntechTemplateHtml(img1DataUrl, img2DataUrl);
      this.handleTemplateHtml(htmlWithImages);
    } else {
      // Fallback to fetching images from URL paths
      const image1Path = this.backendResponse?.image1 || this.backendResponse?.exactModel?.image1;
      const image2Path = this.backendResponse?.image2 || this.backendResponse?.exactModel?.image2;

      this.fetchImagesAsBase64(image1Path, image2Path).then(([img1DataUrl, img2DataUrl]) => {
        // Pass images directly into the template function so they're interpolated
        // as JavaScript template literal expressions — no placeholder replacement needed
        const htmlWithImages = getRuntechTemplateHtml(img1DataUrl, img2DataUrl);
        this.handleTemplateHtml(htmlWithImages);
      });
    }
  }

  /**
   * Format base64 string as a data URL if not already formatted
   */
  private formatBase64DataUrl(base64String: string): string {
    if (!base64String) {
      return '';
    }
    // Check if it's already a data URL
    if (base64String.startsWith('data:')) {
      return base64String;
    }
    // Assume it's JPEG data
    return `data:image/jpeg;base64,${base64String}`;
  }

  /**
   * Fetch image paths from the backend and convert them to base64 data URLs for PDF embedding
   */
  private fetchImagesAsBase64(image1Path?: string, image2Path?: string): Promise<[string, string]> {
    const toDataUrl = (path: string): Promise<string> => {
      if (!path) {
        return Promise.resolve('');
      }
      // Use API base URL from environment configuration or fall back to window origin
      const baseUrl = (environment as any).apiBaseUrl || window.location.origin;
      const url = `${baseUrl}/${path.replace(/^\//, '')}`;
      return this.http
        .get(url, { responseType: 'blob' })
        .toPromise()
        .then((blob: Blob) => {
          return new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => resolve('');
            reader.readAsDataURL(blob);
          });
        })
        .catch(() => '');
    };

    return Promise.all([toDataUrl(image1Path), toDataUrl(image2Path)]);
  }

  // Orchestrator: handle loaded template HTML
  private handleTemplateHtml(html: string) {
    if (!this.backendResponse) {
      console.error('No backend response available for placeholder replacement.');
      this.isPdfDownloading.set(false);
      return;
    }

    const data = { ...(this.fetchSensorValues?.sensorValues ?? {}), ...(this.backendResponse ?? {}) };

    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

    const replacedHtml = this.buildReplacedHtmlFromTemplate(html, data, formattedDate);
    const container = this.createContainerWithHtml(replacedHtml);

    const unitType = this.getAppliedUnitTypeOrSaved();
    const chosenMap = unitType === 'US' ? this.unitMapUS : this.unitMapSI;
    this.patchUnitSystemPlaceholder(container, unitType);

    // Remove Product Views section if both base64 images are missing in backend response
    const image1Base64 = this.backendResponse?.image1Base64;
    const image2Base64 = this.backendResponse?.image2Base64;
    if (!image1Base64 && !image2Base64) {
      this.removeProductViewsSectionIfNoImages(container);
    }

    try {
      if (unitType === 'US') {
        const conversionMap = this.buildConversionMap();
        const conversionObservables = this.buildConversionObservables(data, conversionMap);

        const obsKeys = Object.keys(conversionObservables || {});
        if (obsKeys.length > 0) {
          const ctx = { conversionMap, data, html, container, formattedDate, chosenMap };
          forkJoin(conversionObservables).subscribe(
            (convertedResults: any) => this.handleConversionResults(convertedResults, ctx),
            (convErr: any) => this.handleConversionError(convErr, ctx),
          );
        } else {
          const ctx = { conversionMap, data, html, container, formattedDate, chosenMap };
          this.handleNoAsyncConversions(ctx);
        }
      } else {
        // SI units -> no async conversions
        this.finalizeAfterRebuild(container, chosenMap, html, data, formattedDate);
      }
    } catch (e) {
      console.warn('Failed to patch template units based on unitType', e);
      // proceed with defaults
      this.finalizeAfterRebuild(container, chosenMap, html, data, formattedDate);
    }
  }

  private buildConversionMap(): any[] {
    return [
      { path: 'suction_air_temperature', siUnit: '°C', usUnit: '°F', category: 'temperature' },
      { path: 'suction_air_relative_humidity', siUnit: '%', usUnit: '%', category: 'humidity' },
      { path: 'vacuum', siUnit: 'kPa', usUnit: 'inHg', category: 'pressure' },
      { path: 'suction_volumetric_flow', siUnit: 'm³/s', usUnit: 'CFM', category: 'flow' },
      { path: 'altitude_from_sea_level', siUnit: 'm', usUnit: 'ft', category: 'length' },
      { path: 'dischargeAbsPressure', siUnit: 'Pa', usUnit: 'inHg', category: 'pressure' },
      { path: 'atmosphericPressure', siUnit: 'Pa', usUnit: 'inHg', category: 'pressure' },
      { path: 'interpolationArrays.interpolations.speedInterp', siUnit: 'rpm', usUnit: 'Hz', category: 'speed', isArray: true },
      {
        path: 'interpolationArrays.interpolations.activeGridPowerInterp',
        siUnit: 'kW',
        usUnit: 'BTU/h',
        category: 'power',
        isArray: true,
      },
      {
        path: 'interpolationArrays.interpolations.shaftTorqueInterp',
        siUnit: 'Nm',
        usUnit: 'lbf·ft',
        category: 'torque',
        isArray: true,
      },
      {
        path: 'interpolationArrays.interpolations.newOutputTempInterp',
        siUnit: '°C',
        usUnit: '°F',
        category: 'temperature',
        isArray: true,
      },
      {
        path: 'interpolationArrays.interpolations.newMassFlowInterp',
        siUnit: 'kg/s',
        usUnit: 'lb/s',
        category: 'mass',
        isArray: true,
      },
      {
        path: 'interpolationArrays.interpolations.outletVolumetricFlowInterp',
        siUnit: 'm³/s',
        usUnit: 'CFM',
        category: 'flow',
        isArray: true,
      },
      { path: 'state2RelativeHumidity', siUnit: '%', usUnit: '%', category: 'humidity' },
      { path: 'state1MoistureContentAbs', siUnit: 'gH20/kgda air', usUnit: 'lb/lb_dry_air', category: 'moisture' },
      { path: 'state1MoistureContent', siUnit: 'gH20/kg air', usUnit: 'lb/lb_dry_air', category: 'moisture' },
      { path: 'absolute_pressure_after_recovery', siUnit: 'Pa', usUnit: 'inHg', category: 'pressure' },
      { path: 'temperature_after_recovery', siUnit: '°C', usUnit: '°F', category: 'temperature' },
      { path: 'relative_humidity_after_recovery', siUnit: '%', usUnit: '%', category: 'humidity' },
      { path: 'staticLoad', siUnit: 'kg', usUnit: 'lb', category: 'weight' },
      { path: 'maximumDynamicLoad', siUnit: 'kg', usUnit: 'lb', category: 'weight' },
      { path: 'nominalCoolingAirFlow', siUnit: 'm³/s', usUnit: 'CFM', category: 'flow' },
      { path: 'coolingAirTemperature', siUnit: '°C', usUnit: '°F', category: 'temperature' },
      { path: 'coolingAirPressureDrop', siUnit: 'Pa', usUnit: 'inHg', category: 'pressure' },
    ];
  }

  private buildConversionObservables(data: any, conversionMap: any[]): { [k: string]: any } {
    const conversionObservables: any = {};
    conversionMap.forEach((m) => {
      const val = this.getPathValue(data, m.path);
      if (val === undefined || val === null || val === '') {
        return;
      }
      const numeric = m.isArray && Array.isArray(val) ? val[0] : val;
      const numericVal = typeof numeric === 'number' ? numeric : Number.parseFloat(String(numeric));
      if (Number.isNaN(numericVal)) {
        return;
      }
      if (m.siUnit === m.usUnit) {
        if (m.isArray && Array.isArray(val)) {
          const newArr = [...val];
          newArr[0] = numericVal;
          this.setPathValue(data, m.path, newArr);
        } else {
          this.setPathValue(data, m.path, numericVal);
        }
        return;
      }
      conversionObservables[m.path] = this.unitConversionService.convert(numericVal, m.siUnit, m.usUnit, m.category);
    });
    return conversionObservables;
  }

  private handleConversionResults(convertedResults: any, ctx: any) {
    const { conversionMap, data, html, container, formattedDate, chosenMap } = ctx;
    Object.entries(convertedResults).forEach(([path, convertedValue]) => {
      const mapping = conversionMap.find((m: any) => m.path === path);
      if (!mapping) {
        return;
      }
      if (mapping.isArray) {
        const orig = this.getPathValue(data, path);
        if (Array.isArray(orig)) {
          const newArr = [...orig];
          newArr[0] = convertedValue;
          this.setPathValue(data, path, newArr);
        } else {
          this.setPathValue(data, path, convertedValue);
        }
      } else {
        this.setPathValue(data, path, convertedValue);
      }
    });
    try {
      const newReplacedHtml = this.buildReplacedHtmlFromTemplate(html, data, formattedDate);
      container.innerHTML = newReplacedHtml;
    } catch (rebuildErr) {
      console.warn('Failed to rebuild template after conversions', rebuildErr);
    }
    this.finalizeAfterRebuild(container, chosenMap, html, data, formattedDate);
  }

  private handleConversionError(convErr: any, ctx: any) {
    const { conversionMap, data, html, container, formattedDate, chosenMap } = ctx;
    console.warn('One or more unit conversions failed for PDF generation', convErr);
    try {
      this.applyLocalConversions(conversionMap, data);
    } catch (fallbackErr) {
      console.warn('Local fallback conversions also failed', fallbackErr);
    }
    try {
      const newReplacedHtml = this.buildReplacedHtmlFromTemplate(html, data, formattedDate);
      container.innerHTML = newReplacedHtml;
    } catch (rebuildErr) {
      console.warn('Failed to rebuild template after local fallback conversions', rebuildErr);
    }
    this.finalizeAfterRebuild(container, chosenMap, html, data, formattedDate);
  }

  private handleNoAsyncConversions(ctx: any) {
    const { conversionMap, data, html, container, formattedDate, chosenMap } = ctx;
    try {
      this.applyLocalConversions(conversionMap, data);
    } catch (fallbackErr) {
      console.warn('Local fallback conversions failed (no async conversions)', fallbackErr);
    }
    try {
      const newReplacedHtml = this.buildReplacedHtmlFromTemplate(html, data, formattedDate);
      container.innerHTML = newReplacedHtml;
    } catch (rebuildErr) {
      console.warn('Failed to rebuild template after local conversions (no async)', rebuildErr);
    }
    this.finalizeAfterRebuild(container, chosenMap, html, data, formattedDate);
  }

  private finalizeAfterRebuild(
    container: HTMLDivElement,
    chosenMap: { [k: string]: string },
    _html: string,
    _data: any,
    _formattedDate: string,
  ) {
    this.patchUnitLabels(container, chosenMap);
    // Remove visible table captions and table headers from the DOM used to
    // generate the PDF. We keep them in the original HTML/template for
    // accessibility, but they should not appear in the downloaded PDF.
    try {
      const nodes = Array.from(container.querySelectorAll('.parameter-table caption, .parameter-table thead'));
      nodes.forEach((n) => n.remove());
    } catch (err) {
      // Non-fatal: proceed with PDF generation even if removal fails
      console.warn('Could not remove table captions/headers before PDF generation', err);
    }
    try {
      this.proceedAfterConversions(container);
    } catch (e) {
      console.warn('Error during finalization', e);
      this.finalizeAndGenerate(container);
    }
  }
}
